import { FC, useCallback, useMemo, useState } from "react";
import { View } from "react-native";

import {
  BarChart,
  LineChart,
  useCopyableChart,
  type LineSeries,
} from "@/components/charts";
import { Box, Card, HStack, Pressable, Text, VStack } from "@/components/ui";
import { useLocalization } from "@/hooks";
import SampleEditorialSummary from "./_SampleEditorialSummary";

/* ── Types ─────────────────────────────────────────────── */

export type PriceSegmentKey =
  "too_cheap" | "bargain" | "expensive" | "too_expensive";

export interface PriceElasticityReportData {
  /** Per-segment list of price answers in major currency units. The
      report computes its own histograms and Van Westendorp curves
      from these arrays — cookers don't need to pre-bin. */
  responses: Record<PriceSegmentKey, number[]>;
  currencyCode: string;
}

interface PriceElasticityReportProps {
  data: PriceElasticityReportData;
  showSampleEditorialSummary?: boolean;
}

/* ── Constants ──────────────────────────────────────────── */

const SEGMENTS = [
  { color: "hsl(0, 75%, 60%)", key: "too_cheap" as const },
  { color: "hsl(38, 92%, 50%)", key: "bargain" as const },
  { color: "hsl(217, 91%, 60%)", key: "expensive" as const },
  { color: "hsl(280, 70%, 55%)", key: "too_expensive" as const },
];

/* ── Sample fixture ─────────────────────────────────────── */

/* Deterministic pseudo-random (seeded LCG) so the sample is stable across
   reloads — onboarding screenshots match what the user sees in the app. */
function seededRandom(seed: number): () => number {
  let state = seed;
  return () => {
    state = (state * 1664525 + 1013904223) % 4294967296;
    return state / 4294967296;
  };
}

function normalSample(rng: () => number, mu: number, sigma: number): number {
  const u1 = Math.max(rng(), 1e-9);
  const u2 = rng();
  const z = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
  return Math.max(0, mu + z * sigma);
}

const SAMPLE_RESPONSES: Record<PriceSegmentKey, number[]> = (() => {
  const rng = seededRandom(1337);
  const params: Record<PriceSegmentKey, { mu: number; sigma: number }> = {
    bargain: { mu: 6.5, sigma: 2.4 },
    expensive: { mu: 8.5, sigma: 2.4 },
    too_cheap: { mu: 4.0, sigma: 2.4 },
    too_expensive: { mu: 11.0, sigma: 2.6 },
  };
  const out: Record<string, number[]> = {};
  (Object.keys(params) as PriceSegmentKey[]).forEach((key) => {
    const arr: number[] = [];
    for (let i = 0; i < 250; i += 1) {
      arr.push(normalSample(rng, params[key].mu, params[key].sigma));
    }
    out[key] = arr;
  });
  return out as Record<PriceSegmentKey, number[]>;
})();

export const SAMPLE_PRICE_ELASTICITY_DATA: PriceElasticityReportData = {
  currencyCode: "USD",
  responses: SAMPLE_RESPONSES,
};

/* ── Helpers ────────────────────────────────────────────── */

function histogram(values: number[], minX: number, maxX: number, bins = 14) {
  const width = (maxX - minX) / bins;
  const points: { mid: number; y: number }[] = [];
  for (let i = 0; i < bins; i += 1) {
    const lo = minX + i * width;
    const hi = lo + width;
    const count = values.filter((v) =>
      i === bins - 1 ? v >= lo && v <= hi : v >= lo && v < hi
    ).length;
    points.push({ mid: (lo + hi) / 2, y: count });
  }
  return points;
}

function cumulativeAtOrAbove(values: number[], price: number): number {
  if (values.length === 0) return 0;
  return values.filter((v) => v >= price).length / values.length;
}
function cumulativeAtOrBelow(values: number[], price: number): number {
  if (values.length === 0) return 0;
  return values.filter((v) => v <= price).length / values.length;
}

/** Find the price at which two cumulative curves cross. Linear
    interpolation between adjacent samples; returns null when the
    curves never intersect inside the sampled range. */
function findIntersection(
  a: { x: number; y: number }[],
  b: { x: number; y: number }[]
): { x: number; y: number } | null {
  for (let i = 1; i < a.length; i += 1) {
    const da = a[i].y - b[i].y;
    const dPrev = a[i - 1].y - b[i - 1].y;
    if (da === 0) return { x: a[i].x, y: a[i].y };
    if (dPrev * da < 0) {
      const t = dPrev / (dPrev - da);
      const x = a[i - 1].x + t * (a[i].x - a[i - 1].x);
      const ay = a[i - 1].y + t * (a[i].y - a[i - 1].y);
      return { x, y: ay };
    }
  }
  return null;
}

/* ── Component ──────────────────────────────────────────── */

const PriceElasticityReport: FC<PriceElasticityReportProps> = ({
  data,
  showSampleEditorialSummary = false,
}) => {
  const { i18n, locale } = useLocalization();
  const [selectedSegment, setSelectedSegment] =
    useState<PriceSegmentKey>("bargain");

  const { chartRef: vwRef, copyButton: vwCopyButton } = useCopyableChart();
  const { chartRef: histogramRef, copyButton: histogramCopyButton } =
    useCopyableChart();

  const fmtPrice = useCallback(
    (n: number) =>
      new Intl.NumberFormat(locale, {
        currency: data.currencyCode,
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
        style: "currency",
      }).format(n),
    [locale, data.currencyCode]
  );

  const segmentLabel: Record<PriceSegmentKey, string> = {
    bargain: i18n.t("useCasePage.priceElasticity.questions.bargain.title"),
    expensive: i18n.t("useCasePage.priceElasticity.questions.expensive.title"),
    too_cheap: i18n.t("useCasePage.priceElasticity.questions.tooCheap.title"),
    too_expensive: i18n.t(
      "useCasePage.priceElasticity.questions.tooExpensive.title"
    ),
  };

  /* X-axis range derived from the actual responses, with a 10% margin so
     extreme values don't clip against the chart edges. */
  const { maxX, minX, sampleSize } = useMemo(() => {
    const allValues = SEGMENTS.flatMap((s) => data.responses[s.key] ?? []);
    if (allValues.length === 0) {
      return { maxX: 18, minX: 0, sampleSize: 0 };
    }
    const lo = Math.max(0, Math.min(...allValues) * 0.9);
    const hi = Math.max(...allValues) * 1.1;
    const totalsBySegment = SEGMENTS.map(
      (s) => data.responses[s.key]?.length ?? 0
    );
    return {
      maxX: hi,
      minX: lo,
      sampleSize: Math.max(...totalsBySegment, 0),
    };
  }, [data.responses]);

  const histogramPoints = useMemo(
    () =>
      histogram(data.responses[selectedSegment] ?? [], minX, maxX, 14).map(
        (b) => ({
          x: fmtPrice(b.mid),
          y: b.y,
        })
      ),
    [selectedSegment, data.responses, fmtPrice, minX, maxX]
  );

  const { intersections, series } = useMemo(() => {
    const step = Math.max((maxX - minX) / 64, 0.05);
    const tooCheap: { x: number; y: number }[] = [];
    const bargain: { x: number; y: number }[] = [];
    const expensive: { x: number; y: number }[] = [];
    const tooExpensive: { x: number; y: number }[] = [];

    for (let p = minX; p <= maxX; p += step) {
      tooCheap.push({
        x: p,
        y: cumulativeAtOrAbove(data.responses.too_cheap ?? [], p) * 100,
      });
      bargain.push({
        x: p,
        y: cumulativeAtOrAbove(data.responses.bargain ?? [], p) * 100,
      });
      expensive.push({
        x: p,
        y: cumulativeAtOrBelow(data.responses.expensive ?? [], p) * 100,
      });
      tooExpensive.push({
        x: p,
        y: cumulativeAtOrBelow(data.responses.too_expensive ?? [], p) * 100,
      });
    }

    const seriesByKey: Record<string, { x: number; y: number }[]> = {
      bargain,
      expensive,
      too_cheap: tooCheap,
      too_expensive: tooExpensive,
    };

    const built: LineSeries[] = SEGMENTS.map((s) => ({
      color: s.color,
      key: s.key,
      label: segmentLabel[s.key],
      points: seriesByKey[s.key],
    }));

    return {
      intersections: {
        ipp: findIntersection(bargain, expensive),
        opp: findIntersection(tooCheap, tooExpensive),
        pmc: findIntersection(tooCheap, expensive),
        pme: findIntersection(bargain, tooExpensive),
      },
      series: built,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.responses, i18n, minX, maxX]);

  const { ipp, opp, pmc, pme } = intersections;

  /* No answers yet: render the shell but hand the charts empty data (they show
     their own "No data available") rather than plotting flat zero curves —
     mirroring how `PricingResult` shows "—" rather than a fabricated price. */
  const hasData = sampleSize > 0;

  /* OPP gets promoted to a full-height vertical reference line — it's
     the headline metric of the VW analysis and needs to read at a
     glance. The other three intersections stay as dots so the chart
     doesn't turn into a thicket of vertical lines. */
  const OPP_COLOR = "hsl(160, 84%, 39%)";

  const referenceLines = useMemo(() => {
    if (!opp) return [];
    return [
      {
        color: OPP_COLOR,
        label: `${i18n.t("useCasePage.sampleReport.vw.oppShort")} · ${fmtPrice(
          opp.x
        )}`,
        x: opp.x,
      },
    ];
  }, [opp, fmtPrice, i18n]);

  const referencePoints = useMemo(() => {
    const points = [];
    /* OPP dot rides on the vertical line — no text label, the line
       already carries `OPP · $X.XX` at the top. */
    if (opp)
      points.push({
        ...opp,
        color: OPP_COLOR,
      });
    if (ipp)
      points.push({
        ...ipp,
        color: "hsl(217, 91%, 60%)",
        label: i18n.t("useCasePage.sampleReport.vw.ippShort"),
      });
    if (pmc)
      points.push({
        ...pmc,
        color: "hsl(0, 75%, 60%)",
        label: i18n.t("useCasePage.sampleReport.vw.pmcShort"),
      });
    if (pme)
      points.push({
        ...pme,
        color: "hsl(280, 70%, 55%)",
        label: i18n.t("useCasePage.sampleReport.vw.pmeShort"),
      });
    return points;
  }, [opp, ipp, pmc, pme, i18n]);

  return (
    <VStack className="gap-3">
      {showSampleEditorialSummary && (
        <SampleEditorialSummary
          body={i18n.t("useCasePage.sampleReport.summary.body", {
            ipp: ipp ? fmtPrice(ipp.x) : "n/a",
            opp: opp ? fmtPrice(opp.x) : "n/a",
            pmc: pmc ? fmtPrice(pmc.x) : "n/a",
            pme: pme ? fmtPrice(pme.x) : "n/a",
          })}
          headline={i18n.t("useCasePage.sampleReport.summary.headline", {
            opp: opp ? fmtPrice(opp.x) : "n/a",
            pmc: pmc ? fmtPrice(pmc.x) : "n/a",
            pme: pme ? fmtPrice(pme.x) : "n/a",
          })}
        />
      )}

      <Card className="group p-6 rounded-2xl border border-outline-200">
        {vwCopyButton}
        <VStack className="gap-5">
          <VStack className="gap-1 pb-2">
            <Text className="text-sm font-semibold text-typography-900 dark:text-typography-100">
              {`${i18n.t("useCasePage.sampleReport.vw.title")} (${i18n.t("dashboardNavigator.respondents", { count: sampleSize })})`}
            </Text>
            <Text className="text-xs text-typography-400">
              {i18n.t("useCasePage.sampleReport.vw.subtitle")}
            </Text>
          </VStack>

          <View ref={vwRef} style={{ width: "100%" }}>
            <LineChart
              formatX={fmtPrice}
              formatY={(v) => `${Math.round(v)}%`}
              height={420}
              referenceLines={hasData ? referenceLines : []}
              referencePoints={hasData ? referencePoints : []}
              series={hasData ? series : []}
              xLabel={i18n.t("useCasePage.sampleReport.vw.xLabel")}
              yDomain={[0, 100]}
            />
          </View>

          <HStack className="flex-wrap gap-3 pt-2">
            {opp && (
              <ReferenceTag
                color="hsl(160, 84%, 39%)"
                label={i18n.t("useCasePage.sampleReport.vw.oppShort")}
                sublabel={i18n.t("useCasePage.sampleReport.vw.oppLabel", {
                  price: fmtPrice(opp.x),
                })}
              />
            )}
            {ipp && (
              <ReferenceTag
                color="hsl(217, 91%, 60%)"
                label={i18n.t("useCasePage.sampleReport.vw.ippShort")}
                sublabel={i18n.t("useCasePage.sampleReport.vw.ippLabel", {
                  price: fmtPrice(ipp.x),
                })}
              />
            )}
            {pmc && (
              <ReferenceTag
                color="hsl(0, 75%, 60%)"
                label={i18n.t("useCasePage.sampleReport.vw.pmcShort")}
                sublabel={i18n.t("useCasePage.sampleReport.vw.pmcLabel", {
                  price: fmtPrice(pmc.x),
                })}
              />
            )}
            {pme && (
              <ReferenceTag
                color="hsl(280, 70%, 55%)"
                label={i18n.t("useCasePage.sampleReport.vw.pmeShort")}
                sublabel={i18n.t("useCasePage.sampleReport.vw.pmeLabel", {
                  price: fmtPrice(pme.x),
                })}
              />
            )}
          </HStack>
        </VStack>
      </Card>

      <Card className="group p-6 rounded-2xl border border-outline-200">
        {histogramCopyButton}
        <VStack className="gap-5">
          <VStack className="gap-1 pb-2">
            <Text className="text-sm font-semibold text-typography-900 dark:text-typography-100">
              {`${i18n.t("useCasePage.sampleReport.histogram.title")} (${i18n.t("dashboardNavigator.respondents", { count: sampleSize })})`}
            </Text>
            <Text className="text-xs text-typography-400">
              {i18n.t("useCasePage.sampleReport.histogram.subtitle")}
            </Text>
          </VStack>

          <HStack className="flex-wrap gap-2">
            {SEGMENTS.map((s) => {
              const active = selectedSegment === s.key;
              return (
                <Pressable
                  key={s.key}
                  onPress={() => setSelectedSegment(s.key)}
                >
                  <HStack
                    className={`items-center gap-2 px-3 py-1.5 rounded-full border transition-colors ${
                      active
                        ? "border-typography-900 bg-typography-900"
                        : "border-outline-200 bg-white"
                    }`}
                  >
                    <Box
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: s.color }}
                    />
                    <Text
                      className={`text-xs font-medium ${
                        active ? "text-white" : "text-typography-700"
                      }`}
                    >
                      {segmentLabel[s.key]}
                    </Text>
                  </HStack>
                </Pressable>
              );
            })}
          </HStack>

          <View ref={histogramRef} style={{ width: "100%" }}>
            <BarChart
              points={hasData ? histogramPoints : []}
              totalRespondents={sampleSize}
            />
          </View>
        </VStack>
      </Card>
    </VStack>
  );
};

const ReferenceTag: FC<{ color: string; label: string; sublabel: string }> = ({
  color,
  label,
  sublabel,
}) => (
  <HStack className="items-center gap-2 px-3 py-1.5 rounded-lg border border-outline-100 bg-background-50">
    <Box
      className="w-2.5 h-2.5 rounded-full"
      style={{ backgroundColor: color }}
    />
    <Text className="text-2xs font-bold tracking-wide text-typography-900">
      {label}
    </Text>
    <Text className="text-2xs text-typography-500">{sublabel}</Text>
  </HStack>
);

export default PriceElasticityReport;
